'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth0 } from '@auth0/auth0-react';
import { useLayoutEffect, useState } from 'react';
import { getAccessToken, getAuthSessionEpoch, isAccessTokenAuth0, isLastActiveExpired, registerAuth0LogoutHandler, registerAuth0TokenRefresher, removeRegularAuthTokens, SESSION_EVALUATED_DOM_EVENT, setAuth0AccessToken, unregisterAuth0LogoutHandler, unregisterAuth0TokenRefresher } from '@libs/core';
import { useAuth0SessionLogout } from '../../hooks/useAuth0SessionLogout';
import { logSentryError, markAuth0LoginCompleted, readAuth0ReturnTo, waitForAuth0CartMerge } from '../../utils';
import { auth0TokenSyncSentryTags } from './auth0TokenSyncSentryTags';
import { completeAuth0CallbackRedirect, isAuth0CallbackPath, setPendingAuth0ReturnTo } from './pendingAuth0ReturnTo';
const ACCESS_TOKEN_SYNC_SENTRY = {
  component: 'useAuth0AccessTokenSync',
  action: 'getAccessTokenSilently'
} as const;
type AccessTokenSyncContext = {
  isCurrentEffect: () => boolean;
  getAccessTokenSilently: () => Promise<string>;
  setSyncing: (value: boolean) => void;
  onCallbackError: (error: Error) => void;
  auth0SessionLogout: ReturnType<typeof useAuth0SessionLogout>;
};
function dispatchSessionEvaluated() {
  globalThis.window.dispatchEvent(new CustomEvent(SESSION_EVALUATED_DOM_EVENT));
}
function logAccessTokenSyncError(error: unknown) {
  logSentryError(error, auth0TokenSyncSentryTags(ACCESS_TOKEN_SYNC_SENTRY));
}
function shouldPersistAccessToken(ctx: AccessTokenSyncContext, accessToken: string, epochAtStart: number): boolean {
  return Boolean(ctx.isCurrentEffect() && accessToken && getAuthSessionEpoch() === epochAtStart && !isLastActiveExpired());
}
async function resyncExistingAccessToken(ctx: AccessTokenSyncContext) {
  if (isLastActiveExpired()) {
    await ctx.auth0SessionLogout({});
    return;
  }
  ctx.setSyncing(true);
  const epochAtStart = getAuthSessionEpoch();
  try {
    const accessToken = await ctx.getAccessTokenSilently();
    if (shouldPersistAccessToken(ctx, accessToken, epochAtStart)) {
      setAuth0AccessToken(accessToken);
    }
  } catch (error) {
    logAccessTokenSyncError(error);
  } finally {
    ctx.setSyncing(false);
  }
  if (ctx.isCurrentEffect()) {
    dispatchSessionEvaluated();
  }
}
async function syncUnauthenticatedAccessToken(ctx: AccessTokenSyncContext) {
  if (!isAccessTokenAuth0()) {
    return;
  }
  const existingAccessToken = getAccessToken();
  if (existingAccessToken) {
    // Safari can finish SDK init with `isAuthenticated: false` on cross-site
    // hosts (ephemeral, localhost) before silent auth runs, or when iframe-based
    // renewal is blocked. Keep the legacy cookie and try to re-sync instead of
    // clearing cookies the rest of the app still relies on — unless inactivity
    // already ended the local session, in which case a silent write would
    // reopen cookie-gated routes.
    await resyncExistingAccessToken(ctx);
    return;
  }
  removeRegularAuthTokens();
  dispatchSessionEvaluated();
}
async function syncAuthenticatedAccessToken(ctx: AccessTokenSyncContext) {
  if (isLastActiveExpired()) {
    await ctx.auth0SessionLogout({});
    return;
  }
  ctx.setSyncing(true);
  const epochAtStart = getAuthSessionEpoch();
  let accessToken = '';
  try {
    accessToken = await ctx.getAccessTokenSilently();
  } catch (error) {
    // The Auth0 session is still valid here (we passed the `!isAuthenticated`
    // guard above), so a transient token-fetch failure must not clear the
    // legacy access cookie. On the callback route, surface the failure instead
    // of navigating without the cookie and rendering a logged-out destination.
    const syncError = error instanceof Error ? error : new Error('Failed to sync the Auth0 access token');
    logAccessTokenSyncError(syncError);
    if (ctx.isCurrentEffect() && isAuth0CallbackPath(globalThis.window.location.pathname)) {
      ctx.onCallbackError(syncError);
    }
    return;
  } finally {
    ctx.setSyncing(false);
  }
  if (shouldPersistAccessToken(ctx, accessToken, epochAtStart)) {
    setAuth0AccessToken(accessToken);
    dispatchSessionEvaluated();
    if (isAuth0CallbackPath(globalThis.window.location.pathname)) {
      await redirectAfterCartMerge(ctx);
      return;
    }
    completeAuth0CallbackRedirect();
  }
}
async function redirectAfterCartMerge(ctx: AccessTokenSyncContext) {
  await waitForAuth0CartMerge();
  if (!ctx.isCurrentEffect()) {
    return;
  }
  const postMergeReturnTo = readAuth0ReturnTo();
  if (postMergeReturnTo) {
    setPendingAuth0ReturnTo(postMergeReturnTo);
  }

  // Arm login analytics only when persistAuth0LoginMethod ran before this
  // callback (login/signup). Other Auth0 returns (change-email) must not.
  markAuth0LoginCompleted();
  completeAuth0CallbackRedirect();
}

/**
 * Keeps the legacy `accessToken` cookie in sync with the Auth0 SDK session.
 *
 * Performs an initial sync on auth state changes so the cookie exists before the
 * first API call.
 *
 * Registers demand-driven Auth0 bridges used by core fetch/session layers:
 * token refresh on 401 and session termination on inactivity expiry.
 * Logs out on mount when `lastActive` is already past the inactivity threshold
 * so SSR pages without an immediate client fetch still terminate stale sessions.
 * That teardown runs for both `isAuthenticated` and the Safari path where the SDK
 * reports unauthenticated while a legacy Auth0 `accessToken` cookie is still present.
 *
 * After a Universal Login callback, waits for guest-cart merge, then document-
 * navigates to the pending `returnTo` (preferring a post-merge path) only once
 * the cookie is written so Next.js cookie redirects can run. Token sync failures
 * stay on the callback route and surface through the existing error boundary.
 */
export function useAuth0AccessTokenSync() {
  const $ = _c(6);
  const {
    isLoading,
    isAuthenticated,
    getAccessTokenSilently
  } = useAuth0();
  const auth0SessionLogout = useAuth0SessionLogout();
  const [callbackError, setCallbackError] = useState();
  let t0;
  let t1;
  if ($[0] !== auth0SessionLogout || $[1] !== getAccessTokenSilently || $[2] !== isAuthenticated || $[3] !== isLoading) {
    t0 = () => {
      let isCurrentEffect = true;
      let isSyncing = false;
      registerAuth0TokenRefresher(async () => {
        ;
        try {
          return await getAccessTokenSilently({
            cacheMode: "off"
          });
        } catch (t2) {
          const error = t2;
          logAccessTokenSyncError(error);
          return null;
        }
      });
      registerAuth0LogoutHandler(auth0SessionLogout);
      const ctx = {
        isCurrentEffect: () => isCurrentEffect,
        getAccessTokenSilently,
        setSyncing: value => {
          isSyncing = value;
        },
        onCallbackError: setCallbackError,
        auth0SessionLogout
      };
      const syncAccessToken = async function syncAccessToken() {
        if (isLoading || isSyncing) {
          return;
        }
        if (!isAuthenticated) {
          await syncUnauthenticatedAccessToken(ctx);
          return;
        }
        await syncAuthenticatedAccessToken(ctx);
      };
      syncAccessToken();
      return () => {
        isCurrentEffect = false;
        unregisterAuth0TokenRefresher();
        unregisterAuth0LogoutHandler();
      };
    };
    t1 = [auth0SessionLogout, getAccessTokenSilently, isAuthenticated, isLoading];
    $[0] = auth0SessionLogout;
    $[1] = getAccessTokenSilently;
    $[2] = isAuthenticated;
    $[3] = isLoading;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useLayoutEffect(t0, t1);
  if (callbackError) {
    throw callbackError;
  }
}